/**
 * 语音合成播放器（Phase 4 · Ticket P4-03）
 * 优先使用浏览器 Web Speech API，降级至 Howler.js 播放预生成音频。
 * 支持中文语音、语速控制、事件回调、词级高亮时间戳。
 */

export interface TTSOptions {
  rate?: number;      // 语速 0.1-2.0（默认 0.92，适配儿童）
  pitch?: number;     // 音调 0-2（默认 1.0）
  volume?: number;    // 音量 0-1（默认 1.0）
  lang?: string;      // 语言 'zh-CN' | 'en-US' 等
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (err: any) => void;
  /** 词级时间戳回调：每读到一个词时触发（Phase 4 新增） */
  onWord?: (word: string, index: number, startMs: number, endMs: number) => void;
}

class TTSPlayer {
  private synth: SpeechSynthesis | null = null;
  private _speaking = false;
  private _wordTimers: ReturnType<typeof setTimeout>[] = [];

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synth = window.speechSynthesis;
    }
  }

  get isSupported(): boolean {
    return !!this.synth;
  }

  get isSpeaking(): boolean {
    return this._speaking;
  }

  /**
   * 朗读文本，支持词级高亮回调
   * - 通过 estimated duration + word count 模拟词级时间戳
   * - 实际生产环境应使用后端生成的 timestamps 数据
   */
  speak(text: string, options: TTSOptions = {}) {
    if (!this.synth) {
      options.onError?.(new Error('浏览器不支持语音合成'));
      return;
    }

    // 取消之前的朗读和时间戳定时器
    this.stop();

    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = options.rate ?? 0.92;
    utter.pitch = options.pitch ?? 1.0;
    utter.volume = options.volume ?? 1.0;
    utter.lang = options.lang ?? 'zh-CN';

    // 优先选中文语音
    const voices = this.synth.getVoices();
    const zhVoice = voices.find((v) => v.lang.startsWith('zh') && v.name.includes('Female'))
      || voices.find((v) => v.lang.startsWith('zh'));
    if (zhVoice) utter.voice = zhVoice;

    // 预分词：中文按字，英文按空格
    const words = this._tokenize(text);

    // 估算总时长（秒）
    const estimatedDuration = this._estimateDuration(text, utter.rate);
    const wordDuration = estimatedDuration / Math.max(words.length, 1);

    utter.onstart = () => {
      this._speaking = true;
      options.onStart?.();

      // 设置词级时间戳定时器
      if (options.onWord) {
        words.forEach((word, index) => {
          const startMs = Math.round(index * wordDuration * 1000);
          const endMs = Math.round((index + 1) * wordDuration * 1000);
          const timer = setTimeout(() => {
            if (this._speaking) {
              options.onWord!(word, index, startMs, endMs);
            }
          }, startMs);
          this._wordTimers.push(timer);
        });
      }
    };

    utter.onend = () => {
      this._speaking = false;
      this._clearWordTimers();
      options.onEnd?.();
    };

    utter.onerror = (e) => {
      this._speaking = false;
      this._clearWordTimers();
      options.onError?.(e);
    };

    this.synth.speak(utter);
  }

  stop() {
    if (this.synth) {
      this.synth.cancel();
    }
    this._speaking = false;
    this._clearWordTimers();
  }

  pause() {
    this.synth?.pause();
  }

  resume() {
    this.synth?.resume();
  }

  /**
   * P5-01: 多角色序列播放
   * segments: [{ speaker: 'narrator', text: '从前有座山' }, ...]
   * voiceMap: { narrator: { rate: 0.92, pitch: 1.0 }, '小明': { rate: 1.0, pitch: 1.2 } }
   */
  speakMultiRole(
    segments: Array<{ speaker: string; text: string }>,
    voiceMap: Record<string, Partial<TTSOptions>> = {},
    options: {
      onSegmentStart?: (speaker: string, text: string, index: number) => void;
      onSegmentEnd?: (speaker: string, text: string, index: number) => void;
      onWord?: (word: string, index: number, startMs: number, endMs: number, speaker: string) => void;
      onComplete?: () => void;
      onError?: (err: any) => void;
    } = {}
  ) {
    if (!this.synth) {
      options.onError?.(new Error('浏览器不支持语音合成'));
      return;
    }

    this.stop();

    let currentIndex = 0;

    const playNext = () => {
      if (currentIndex >= segments.length) {
        options.onComplete?.();
        return;
      }

      const segment = segments[currentIndex];
      const voiceConfig = voiceMap[segment.speaker] || {};

      options.onSegmentStart?.(segment.speaker, segment.text, currentIndex);

      const utter = new SpeechSynthesisUtterance(segment.text);
      utter.rate = voiceConfig.rate ?? 0.92;
      utter.pitch = voiceConfig.pitch ?? 1.0;
      utter.volume = voiceConfig.volume ?? 1.0;
      utter.lang = voiceConfig.lang ?? 'zh-CN';

      // 选语音
      const voices = this.synth!.getVoices();
      const preferredLang = utter.lang;
      const speakerVoice = voices.find((v) => v.lang.startsWith(preferredLang) && v.name.includes('Female'))
        || voices.find((v) => v.lang.startsWith(preferredLang));
      if (speakerVoice) utter.voice = speakerVoice;

      // 词级时间戳
      const words = this._tokenize(segment.text);
      const estimatedDuration = this._estimateDuration(segment.text, utter.rate);
      const wordDuration = estimatedDuration / Math.max(words.length, 1);

      utter.onstart = () => {
        this._speaking = true;
        if (options.onWord) {
          words.forEach((word, wIndex) => {
            const startMs = Math.round(wIndex * wordDuration * 1000);
            const endMs = Math.round((wIndex + 1) * wordDuration * 1000);
            const timer = setTimeout(() => {
              if (this._speaking) {
                options.onWord!(word, wIndex, startMs, endMs, segment.speaker);
              }
            }, startMs);
            this._wordTimers.push(timer);
          });
        }
      };

      utter.onend = () => {
        this._clearWordTimers();
        options.onSegmentEnd?.(segment.speaker, segment.text, currentIndex);
        currentIndex += 1;
        playNext();
      };

      utter.onerror = (e) => {
        this._clearWordTimers();
        options.onError?.(e);
      };

      this.synth!.speak(utter);
    };

    playNext();
  }

  /** 分词：中文按字，英文按空格 */
  private _tokenize(text: string): string[] {
    const isChinese = (char: string) => /\p{Script=Han}/u.test(char);
    const result: string[] = [];
    let current = '';
    for (const char of text) {
      if (isChinese(char)) {
        if (current) {
          result.push(...current.trim().split(/\s+/).filter(Boolean));
          current = '';
        }
        result.push(char);
      } else if (/\s/.test(char)) {
        if (current) {
          result.push(...current.trim().split(/\s+/).filter(Boolean));
          current = '';
        }
      } else {
        current += char;
      }
    }
    if (current) {
      result.push(...current.trim().split(/\s+/).filter(Boolean));
    }
    return result;
  }

  /** 估算朗读时长（秒）：中文 ~3.5 chars/s，英文 ~4.5 words/s */
  private _estimateDuration(text: string, rate: number): number {
    const chineseChars = (text.match(/\p{Script=Han}/gu) || []).length;
    const englishWords = text.split(/\s+/).filter(w => /[a-zA-Z]/.test(w)).length;
    const baseDuration = chineseChars / 3.5 + englishWords / 4.5;
    return baseDuration / Math.max(rate, 0.1);
  }

  private _clearWordTimers() {
    this._wordTimers.forEach(clearTimeout);
    this._wordTimers = [];
  }
}

export const ttsPlayer = new TTSPlayer();

/**
 * 预生成音频播放器（Howler.js 封装）
 * 当后端提供 audio_url 时使用。
 */
import { Howl } from 'howler';

export class HowlPlayer {
  private howl: Howl | null = null;
  private _playing = false;
  private _wordTimers: ReturnType<typeof setTimeout>[] = [];

  get isPlaying(): boolean {
    return this._playing;
  }

  play(url: string, options: {
    onStart?: () => void;
    onEnd?: () => void;
    onError?: (err: any) => void;
    /** 词级时间戳回调（需配合后端 timestamps 数据） */
    onWord?: (word: string, index: number, startMs: number, endMs: number) => void;
    timestamps?: Array<{ word: string; start: number; end: number }>;
  } = {}) {
    this.stop();

    this.howl = new Howl({
      src: [url],
      html5: true,
      onplay: () => {
        this._playing = true;
        options.onStart?.();

        // 词级时间戳定时器（配合后端 timestamps）
        if (options.onWord && options.timestamps && options.timestamps.length > 0) {
          options.timestamps.forEach((ts, index) => {
            const timer = setTimeout(() => {
              if (this._playing) {
                options.onWord!(ts.word, index, ts.start, ts.end);
              }
            }, ts.start);
            this._wordTimers.push(timer);
          });
        }
      },
      onend: () => {
        this._playing = false;
        this._clearWordTimers();
        options.onEnd?.();
      },
      onloaderror: (_id, err) => {
        this._playing = false;
        this._clearWordTimers();
        options.onError?.(err);
      },
      onplayerror: (_id, err) => {
        this._playing = false;
        this._clearWordTimers();
        options.onError?.(err);
      },
    });

    this.howl.play();
  }

  stop() {
    if (this.howl) {
      this.howl.stop();
      this.howl.unload();
      this.howl = null;
    }
    this._playing = false;
    this._clearWordTimers();
  }

  pause() {
    this.howl?.pause();
  }

  resume() {
    this.howl?.play();
  }

  private _clearWordTimers() {
    this._wordTimers.forEach(clearTimeout);
    this._wordTimers = [];
  }
}

export const howlPlayer = new HowlPlayer();
